// components/Alerta.tsx
import { useEffect, useState } from "react";
import { ButtonClose } from "../ui";
import { AlertaProps } from "@/types";

const ALERT_STYLES = {
  success: {
    bar: "bg-emerald-500",
    glow: "shadow-emerald-500/30",
    icon: "text-emerald-500",
  },
  error: {
    bar: "bg-red-500",
    glow: "shadow-red-500/30",
    icon: "text-red-500",
  },
  warning: {
    bar: "bg-amber-400",
    glow: "shadow-amber-400/30",
    icon: "text-amber-500",
  },
  info: {
    bar: "bg-sky-500",
    glow: "shadow-sky-500/30",
    icon: "text-sky-500",
  },
};

const EXIT_DELAY = 1000; // ⏸ pausa tras completar la barra

const Alerta: React.FC<AlertaProps> = ({
  mensaje,
  onClose,
  type = "success",
  className = "",
  autoProgress = true,
  duration = 1500,
  children,
}) => {
  const [visible, setVisible] = useState(false);

  const style =
    ALERT_STYLES[type as keyof typeof ALERT_STYLES] || ALERT_STYLES.info;

    useEffect(() => {
      if (!mensaje) return;
    
      setVisible(true);
    
      if (!autoProgress) return;
    
      const timer = setTimeout(() => {
        handleClose();
      }, duration + EXIT_DELAY);
    
      return () => clearTimeout(timer);
    }, [mensaje]);
    

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 250);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`
          relative w-full max-w-md overflow-hidden
          rounded-2xl bg-white/90 backdrop-blur-xl
          shadow-2xl ${style.glow}
          transform transition-all duration-300 ease-out
          ${visible
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-6 scale-95"
          }
          ${className}
        `}
      >
        {/* Barra lateral */}
        <div className={`absolute left-0 top-0 h-full w-1.5 ${style.bar}`} />

        {/* Contenido */}
        <div className="flex items-start gap-4 p-6">
          <div className={`text-xl ${style.icon}`}>●</div>

          <div className="flex-1 text-zinc-800">
            <p className="text-base font-medium leading-relaxed">
              {mensaje}
            </p>
            {children && <div className="mt-4">{children}</div>}
          </div>

          <ButtonClose
            onClick={handleClose}
            className="text-zinc-400 hover:text-zinc-700 transition"
          />
        </div>

        {/* ⏳ Barra inferior animada */}
        {autoProgress && (
          <div className="absolute bottom-0 left-0 h-1 w-full bg-zinc-200/40 overflow-hidden">
            <div
              className={`h-full ${style.bar}`}
              style={{
                animation: `alert-progress ${duration}ms linear forwards`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Alerta;
